import { useState, useCallback, useRef, useEffect } from 'react';

export interface SpeechRecognitionOptions {
  continuous?: boolean;
  interimResults?: boolean;
  lang?: string;
  maxAlternatives?: number;
}

export interface SpeechRecognitionError {
  type:
    | 'not-allowed'
    | 'no-speech'
    | 'audio-capture'
    | 'network'
    | 'aborted'
    | 'service-not-allowed'
    | 'bad-grammar'
    | 'language-not-supported';
  message: string;
}

export interface SpeechRecognitionState {
  isListening: boolean;
  isSupported: boolean;
  transcript: string;
  interimTranscript: string;
  error: SpeechRecognitionError | null;
}

export interface SpeechRecognitionCallbacks {
  onResult?: (transcript: string, isFinal: boolean) => void;
  onError?: (error: SpeechRecognitionError) => void;
  onStart?: () => void;
  onEnd?: () => void;
}

type BrowserSpeechRecognition = InstanceType<Window['SpeechRecognition']>;

export const useSpeechRecognition = (
  options: SpeechRecognitionOptions = {},
  callbacks: SpeechRecognitionCallbacks = {},
) => {
  const {
    continuous = false,
    interimResults = false,
    lang = 'en-US',
    maxAlternatives = 1,
  } = options;

  const { onResult, onError, onStart, onEnd } = callbacks;

  const [state, setState] = useState<SpeechRecognitionState>({
    isListening: false,
    isSupported: false,
    transcript: '',
    interimTranscript: '',
    error: null,
  });

  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const isSupportedRef = useRef<boolean>(false);

  // Check for browser support
  useEffect(() => {
    const isSupported =
      'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

    isSupportedRef.current = isSupported;
    setState((prev) => ({ ...prev, isSupported }));
  }, []);

  // Initialize recognition instance
  const initializeRecognition = useCallback(() => {
    if (!isSupportedRef.current) {
      return null;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    const recognition = new SpeechRecognition();

    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = lang;
    recognition.maxAlternatives = maxAlternatives;

    recognition.onstart = () => {
      setState((prev) => ({
        ...prev,
        isListening: true,
        error: null,
      }));
      onStart?.();
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const { transcript } = result[0];

        if (result.isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      setState((prev) => ({
        ...prev,
        transcript: prev.transcript + finalTranscript,
        interimTranscript,
      }));

      if (finalTranscript) {
        onResult?.(finalTranscript, true);
      }
      if (interimTranscript) {
        onResult?.(interimTranscript, false);
      }
    };

    recognition.onerror = (event) => {
      const errorMap: Record<string, SpeechRecognitionError> = {
        'not-allowed': {
          type: 'not-allowed',
          message:
            'Microphone access denied. Please allow microphone access to use voice input.',
        },
        'no-speech': {
          type: 'no-speech',
          message: 'No speech detected. Please try again.',
        },
        'audio-capture': {
          type: 'audio-capture',
          message:
            'No microphone was found. Please ensure a microphone is connected.',
        },
        network: {
          type: 'network',
          message:
            'Network error occurred. Please check your internet connection.',
        },
        aborted: {
          type: 'aborted',
          message: 'Speech recognition was aborted.',
        },
        'service-not-allowed': {
          type: 'service-not-allowed',
          message: 'Speech recognition service is not allowed.',
        },
        'bad-grammar': {
          type: 'bad-grammar',
          message: 'Speech recognition grammar error.',
        },
        'language-not-supported': {
          type: 'language-not-supported',
          message: 'The selected language is not supported.',
        },
      };

      const error = errorMap[event.error] || {
        type: 'network' as const,
        message: 'Speech recognition error occurred. Please try again.',
      };

      setState((prev) => ({
        ...prev,
        isListening: false,
        error,
      }));

      onError?.(error);
    };

    recognition.onend = () => {
      setState((prev) => ({ ...prev, isListening: false }));
      onEnd?.();
    };

    return recognition;
  }, [
    continuous,
    interimResults,
    lang,
    maxAlternatives,
    onResult,
    onError,
    onStart,
    onEnd,
  ]);

  const startListening = useCallback(() => {
    if (!isSupportedRef.current) {
      const error: SpeechRecognitionError = {
        type: 'service-not-allowed',
        message: 'Speech recognition is not supported in this browser',
      };
      setState((prev) => ({ ...prev, error }));
      onError?.(error);
      return;
    }

    if (!recognitionRef.current) {
      recognitionRef.current = initializeRecognition();
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
      } catch (err) {
        const error: SpeechRecognitionError = {
          type: 'aborted',
          message: 'Failed to start speech recognition. Please try again.',
        };
        setState((prev) => ({ ...prev, error }));
        onError?.(error);
      }
    }
  }, [initializeRecognition, onError]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && state.isListening) {
      recognitionRef.current.stop();
    }
  }, [state.isListening]);

  const resetTranscript = useCallback(() => {
    setState((prev) => ({
      ...prev,
      transcript: '',
      interimTranscript: '',
      error: null,
    }));
  }, []);

  const clearError = useCallback(
    () => setState((prev) => ({ ...prev, error: null })),
    [],
  );

  const resetRecognition = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      isListening: false,
      transcript: '',
      interimTranscript: '',
      error: null,
    }));
  }, []);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    },
    [],
  );

  return {
    ...state,
    startListening,
    stopListening,
    resetTranscript,
    clearError,
    resetRecognition,
  };
};
